// The last failed calls this browser tab saw (pilot feedback, release 1.2.0, O-R34), ported from Summit Learning's APEX
// error ring buffer (D-0080). Recorded by the API client for every failed call except sign-in refusals; read only when
// the person presses "Pilot feedback" and chooses to send them. Tokens, secrets, email addresses and query strings are
// removed before anything is kept.

export interface PilotErrorLogEntry {
  status: number;
  title: string | null;
  correlationId: string | null;
  path: string | null;
  atUtc: string | null;
}

const MAX_ERRORS = 10;
const ERROR_WINDOW_MS = 5 * 60 * 1000;
const recentErrors: (PilotErrorLogEntry & { atMs: number })[] = [];

const TOKENISH = /(bearer\s+\S+|eyJ[\w-]+\.[\w-]+\.[\w-]*|(token|code|sig|key|password|secret)=[^\s&]+|[\w.%+-]+@[\w.-]+\.[a-z]{2,})/gi;

export function sanitizePilotText(text: string | null | undefined, max = 300): string | null {
  if (!text) return null;
  const cleaned = text.replace(TOKENISH, "[removed]").replace(/\s+/g, " ").trim();
  if (!cleaned) return null;
  return cleaned.length > max ? cleaned.slice(0, max) : cleaned;
}

export function sanitizePilotPath(path: string | null | undefined): string | null {
  if (!path) return null;
  let p = path.trim();
  try {
    if (/^https?:\/\//i.test(p)) p = new URL(p).pathname;
  } catch {
    /* keep as typed */
  }
  const cut = p.search(/[?#]/);
  if (cut >= 0) p = p.slice(0, cut);
  return p ? p.slice(0, 200) : null;
}

export function recordPilotError(
  entry: { status: number; title?: string | null; correlationId?: string | null; path?: string | null },
  nowMs = Date.now()
): void {
  recentErrors.unshift({
    status: entry.status,
    title: sanitizePilotText(entry.title),
    correlationId: sanitizePilotText(entry.correlationId, 100),
    path: sanitizePilotPath(entry.path),
    atUtc: new Date(nowMs).toISOString(),
    atMs: nowMs
  });
  recentErrors.length = Math.min(recentErrors.length, MAX_ERRORS);
}

/** The failed calls of the last five minutes, newest first, at most five (the server keeps five). */
export function recentPilotErrors(nowMs = Date.now()): PilotErrorLogEntry[] {
  return recentErrors
    .filter((e) => nowMs - e.atMs <= ERROR_WINDOW_MS)
    .slice(0, 5)
    .map((e) => ({ status: e.status, title: e.title, correlationId: e.correlationId, path: e.path, atUtc: e.atUtc }));
}

export function clearPilotErrors(): void {
  recentErrors.length = 0;
}
